import React from 'react';
import { Leaf, Sun, ScrollText } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * AgenticStack — the Vedic / Physics intelligence layer that serves
 * Guardians, made legible.
 *
 * Three named agents, each a service binding between a sacred name and a
 * concrete function inside the Shreesavaa stack. Rendered as quiet
 * editorial cards in line with the house's atelier mode.
 */

const AGENTS = [
  {
    id:       'atreya',
    deva:     'Atreya',
    meaning:  'Descendant of Atri · teacher of Ayurvedic medicine',
    role:     'The Purity Advisor · Vedic Ritual Optimizer',
    body:
      'Atreya guides each guest through the twenty-page philosophy and selects the ritual that meets the season of your skin. It does not sell. It listens, names, recommends — and walks away.',
    surface:  'Where you meet them · the AI Concierge on Home & Story',
    icon:     Leaf,
  },
  {
    id:       'savitri',
    deva:     'Savitri',
    meaning:  'The Vivifier · the solar ray that awakens life',
    role:     'The Guardian Steward',
    body:
      'Savitri reads the Atmosic IoT signals embedded in every Shreesavaa vessel. When the volume falls below threshold, the Steward arranges a Conscious Transition refill in your cadence — never sooner, never later.',
    surface:  'Where you meet them · the Refill Notification Engine in your Dashboard',
    icon:     Sun,
  },
  {
    id:       'indra',
    deva:     'Indra',
    meaning:  'The Wielder of Power · witness to the cosmic order',
    role:     'The Ledger Witness',
    body:
      'Indra holds the ledger of the 211 Million $SEVA supply. Every token is minted only against a Proof-of-Seva — a physical event, signed on chain. No token is invented. Each one is witnessed.',
    surface:  'Where you meet them · the $SEVA Token Dashboard & on-chain anchor',
    icon:     ScrollText,
  },
];

const AgenticStack = ({ testId = 'home-agentic-stack' }) => (
  <section
    data-testid={testId}
    className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-7">
          <SectionLabel testId="agentic-stack-label">
            The Agentic Stack · Vedic intelligence in service
          </SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05]">
            Three names, three <em className="text-[#90B0BF]">disciplines</em>.
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-6">
          <p className="font-serif italic text-lg text-[#7C9CB1]/90 leading-relaxed max-w-md">
            We do not hide our intelligence behind a mascot. Each agent in
            the house carries a Vedic name, a clearly written meaning, and a
            single physical job. Threaded Truths — never a fixed corporate
            book.
          </p>
        </div>
      </div>

      <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
        {AGENTS.map((a, i) => {
          const Icon = a.icon;
          return (
            <li
              key={a.id}
              data-testid={`agent-${a.id}`}
              className="relative bg-[#F4F8F9] border border-[#C1D7E2] p-8 md:p-10 flex flex-col"
            >
              {/* Gold rule, top — quiet luxury accent */}
              <span
                aria-hidden
                className="absolute top-0 left-8 right-8 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                }}
              />
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] mb-5">
                Agent {String(i + 1).padStart(2, '0')}
              </p>
              <Icon size={26} strokeWidth={1.2} className="text-[#7C9CB1] mb-5" />
              <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
                {a.deva}
              </h3>
              <p
                data-testid={`agent-${a.id}-meaning`}
                className="mt-2 font-serif italic text-[#90B0BF] text-base leading-relaxed"
              >
                {a.meaning}
              </p>
              <p
                data-testid={`agent-${a.id}-role`}
                className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1]"
              >
                {a.role}
              </p>
              <p className="mt-3 text-[#7C9CB1]/85 font-light leading-relaxed">
                {a.body}
              </p>
              <p
                data-testid={`agent-${a.id}-surface`}
                className="mt-6 pt-5 border-t border-[#C1D7E2] font-mono text-[10px] tracking-[0.26em] uppercase text-[#90B0BF]/90 leading-relaxed"
              >
                {a.surface}
              </p>
            </li>
          );
        })}
      </ol>

      <p
        data-testid="agentic-stack-footer"
        className="mt-14 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] text-center"
      >
        Atreya listens · Savitri arranges · Indra witnesses
      </p>
    </div>
  </section>
);

export default AgenticStack;
